import { gql } from '@apollo/client';
import { type DateRange, type EventFilters, EventTrigger, type Media, MediaType, type PageableContent, type PageableContentAPI, type UUID } from '@cobliteam/common-types';
import { camelCase } from 'change-case';
import graphQLRequester from '../../request/GraphQLRequester';
import type { MediaAPI } from './definitions';
import { toMediaPage } from './mediaTransformer';

export class GraphQLCameraMediaRequester {
  private queryGetMedia = gql`
    query getMedia(
      $driverIds: [ID]
      $vehicleIds: [ID]
      $eventTriggers: [EventTrigger]
      $severities: [Severity]
      $requestStatus: [MediaRequestStatus]
      $fromTimestamp: TimestampInMillis
      $mediaTypes: [MediaType]
      $page: Int
      $size: Int
      $sort: [String]
      $sortList: [String]
      $toTimestamp: TimestampInMillis
      $createdBy: [ID]
      $isNew: Boolean
      $fromCreatedAtTimestamp: TimestampInMillis
      $toCreatedAtTimestamp: TimestampInMillis
      $isFavorite: Boolean
      $safetyOccurrenceIds: [ID]
      $mediaStatus: [String]
      $requestOrigins: [RequestOrigin]
      $descriptionTextQuery: String
      $suggestedOccurrences: [String]
    ) {
      media(
        driverIds: $driverIds
        vehicleIds: $vehicleIds
        eventTriggers: $eventTriggers
        severities: $severities
        fromTimestamp: $fromTimestamp
        requestStatus: $requestStatus
        mediaTypes: $mediaTypes
        page: $page
        size: $size
        sort: $sort
        sortList: $sortList
        toTimestamp: $toTimestamp
        createdBy: $createdBy
        isNew: $isNew
        fromCreatedAtTimestamp: $fromCreatedAtTimestamp
        toCreatedAtTimestamp: $toCreatedAtTimestamp
        isFavorite: $isFavorite
        safetyOccurrenceIds: $safetyOccurrenceIds
        mediaStatus: $mediaStatus
        requestOrigins: $requestOrigins
        descriptionTextQuery: $descriptionTextQuery
        suggestedOccurrences: $suggestedOccurrences
      ) {
        content {
          id
          device_id
          driver_id
          driver {
            id
            name
          }
          unreviewed_driver {
            id
            name
          }
          media_type
          media_status
          safety_review {
            id
            fleet_id
            status
            driver_id
            driver {
              id
              name
            }
            created_by
            updated_by
            updated_by_user {
              full_name
            }
            created_at
            updated_at
            occurrences {
              id
              name
            }
            dismiss_reason
          }
          media_details {
            ... on EventVideo {
              event_trigger
              latitude
              longitude
              event_media_url: media_url
              speed
              speed_sign
              event_thumbnail_url: thumbnail_url
            }
            ... on FootageVideo {
              created_at
              processed_at
              duration
              end_latitude
              end_longitude
              footage_media_url: media_url
              request_id
              start_latitude
              start_longitude
              status
              request_status
              footage_thumbnail_url: thumbnail_url
              updated_at
              created_by {
                id
                full_name
                fleet_id
                email_address
              }
              created_by_trigger {
                id
                name
              }
            }
          }
          vehicle {
            id
            driver {
              id
              name
              phoneNumbers
            }
            model {
              brand
              name
            }
            licensePlate
            alias
          }
          vehicle_group {
            id
            name
          }
          media_view_count
          timestamp
          severity
          is_favorite
          ai_suggested_driver {
            id
            name
          }
          ai_video_tags {
            seatbelt_detection {
              with_seatbelt
            }
          }
        }
        empty
        first
        last
        number
        number_of_elements
        size
        total_elements
        total_pages
      }
    }
  `;

  private queryGetDeviceMediaCount = gql`
    query getDeviceMediaCount(
      $fromTimestamp: TimestampInMillis
      $toTimestamp: TimestampInMillis
      $mediaTypes: [MediaType]
      $vehicleIds: [ID]
      $eventTriggers: [EventTrigger]
    ) {
      media(
        vehicleIds: $vehicleIds
        fromTimestamp: $fromTimestamp
        toTimestamp: $toTimestamp
        mediaTypes: $mediaTypes
        eventTriggers: $eventTriggers
      ) {
        total_elements
      }
    }
  `;

  private getArrayKeys = (obj?: any[]) => {
    if (obj && obj.length > 0) return obj;
    return undefined;
  };

  // A expansão TAILGATING <-> FRONT_COLLISION_WARNING saía daqui, mas passou a depender da flag
  // `camera-split-collision-events` e este módulo é biblioteca pura de request — não lê LaunchDarkly.
  // Agora vive em `useCollisionEventSplit().expandEventTriggers`, aplicada por quem monta os filtros
  // (ADR 0018). Sobrou a expansão que não depende de flag.
  private normalizeEventTriggers = (eventTriggers?: EventTrigger[]): EventTrigger[] => {
    return eventTriggers
      ? [
          ...new Set([
            // LEFT_TURN_SPEEDING is presented to customers as harsh cornering, so filtering by
            // HARSH_CORNERING must also match it (it is never a UI option on its own)
            ...(eventTriggers.includes(EventTrigger.HARSH_CORNERING) ? [EventTrigger.LEFT_TURN_SPEEDING] : []),
            ...eventTriggers,
          ]),
        ]
      : [];
  };

  public getMediaEventList = (filters: EventFilters, pageParam?: number | unknown): Promise<PageableContent<Media>> => {
    const sortList = filters.sort
      ? [filters.sort].flatMap(({ column, order }) => {
          if (column === 'severity') return [`severity.ordinal,${order}`, 'startTime,desc'];
          return `${camelCase(column)},${order}`;
        })
      : undefined;

    return graphQLRequester
      .query<{ media: PageableContentAPI<MediaAPI> }>({
        query: this.queryGetMedia,
        variables: {
          driverIds: this.getArrayKeys(filters.driverIds),
          vehicleIds: this.getArrayKeys(filters.vehicleIds),
          eventTriggers: this.getArrayKeys(this.normalizeEventTriggers(filters.eventTriggers)),
          mediaTypes: this.getArrayKeys(filters.mediaTypes),
          severities: this.getArrayKeys(filters.severityTypes),
          fromTimestamp: filters.mediaPeriod ? filters.mediaPeriod.start : undefined,
          toTimestamp: filters.mediaPeriod ? filters.mediaPeriod.end : undefined,
          page: pageParam ? pageParam : filters.page,
          size: filters.size,
          sortList,
          isFavorite: filters.favoriteMedia ? true : undefined,
          safetyOccurrenceIds: this.getArrayKeys(filters.safetyOccurrenceIds),
          mediaStatus: this.getArrayKeys(filters.mediaStatus),
          descriptionTextQuery: filters.descriptionTextQuery,
          suggestedOccurrences: this.getArrayKeys(filters.suggestedOccurrences),
        },
      })
      .then((result) => toMediaPage(result.media));
  };

  public getMediaEventCount = (vehicleId: UUID, period: DateRange): Promise<number> =>
    graphQLRequester
      .query<{ media: PageableContentAPI<MediaAPI> }>({
        query: this.queryGetDeviceMediaCount,
        variables: {
          mediaTypes: [MediaType.EVENT_VIDEO],
          vehicleIds: [vehicleId],
          fromTimestamp: period.start,
          toTimestamp: period.end,
          eventTriggers: Object.values(EventTrigger).filter((item) => item !== EventTrigger.UNKNOWN),
        },
      })
      .then((result) => result.media.total_elements);
}

export default GraphQLCameraMediaRequester;
